import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CompanyFooter } from '../components/CompanyFooter'

interface PricingTier {
  plano: 'FREE' | 'STARTER' | 'PRO'
  displayName: string
  tagline: string
  priceBrlMonthly: number
  priceBrlYearly: number
  highlight?: boolean
  cta: string
  ctaUrl: string
  features: string[]
  limits: {
    monthlyDocuments: number
    monthlySignatures: number
    maxSignersPerDoc: number
    maxFileSizeMb: number
    retentionDays: number
    apiRateLimitPerMin: number
    whiteLabelEnabled: boolean
    apiAccessEnabled: boolean
  }
}

function fmtLimit(n: number, suffix = ''): string {
  if (n === 0) return 'Ilimitado'
  return `${n.toLocaleString('pt-BR')}${suffix}`
}

function fmtPrice(n: number): string {
  if (n === 0) return 'Grátis'
  return `R$ ${n.toLocaleString('pt-BR')}`
}

export default function Pricing() {
  const [plans, setPlans] = useState<PricingTier[]>([])
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('yearly')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/api/pricing/plans')
      .then(r => r.json())
      .then(d => setPlans(d.plans))
      .catch(() => null)
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-indigo-600">PAKT</Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/ajuda" className="text-gray-600 hover:text-indigo-600">Ajuda</Link>
            <Link to="/login" className="text-gray-600 hover:text-indigo-600">Entrar</Link>
            <Link to="/register" className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg">Começar grátis</Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-16">
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Preços simples e transparentes</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Pagamento mensal ou anual com desconto. Sem fidelidade, sem letra miúda.
            Tudo já vem com validade jurídica plena.
          </p>

          {/* Toggle mensal/anual */}
          <div className="inline-flex items-center bg-white rounded-full border border-gray-200 p-1 mt-8 shadow-sm">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${
                billing === 'monthly' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setBilling('yearly')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${
                billing === 'yearly' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Anual <span className="text-xs ml-1 opacity-75">-17%</span>
            </button>
          </div>
        </header>

        {loading ? (
          <p className="text-center text-gray-500">Carregando...</p>
        ) : (
          <>
            {/* Cards */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              {plans.map(p => {
                const monthlyDisplay = billing === 'yearly' ? Math.round(p.priceBrlYearly / 12) : p.priceBrlMonthly
                const yearlyPrice = p.priceBrlYearly
                return (
                  <article key={p.plano} className={`bg-white rounded-2xl p-6 flex flex-col ${
                    p.highlight ? 'border-2 border-indigo-600 shadow-lg relative' : 'border border-gray-200'
                  }`}>
                    {p.highlight && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        Mais popular
                      </span>
                    )}
                    <h2 className="text-xl font-bold text-gray-900">{p.displayName}</h2>
                    <p className="text-sm text-gray-600 mt-1 min-h-[2.5rem]">{p.tagline}</p>

                    <div className="my-6">
                      {monthlyDisplay === 0 ? (
                        <div className="text-4xl font-bold text-gray-900">Grátis</div>
                      ) : (
                        <>
                          <div className="text-4xl font-bold text-gray-900">{fmtPrice(monthlyDisplay)}<span className="text-base font-normal text-gray-500">/mês</span></div>
                          {billing === 'yearly' && (
                            <p className="text-xs text-gray-500 mt-1">
                              {fmtPrice(yearlyPrice)} cobrados anualmente
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    <a
                      href={p.ctaUrl}
                      className={`block text-center px-4 py-2.5 rounded-lg font-semibold text-sm mb-6 ${
                        p.highlight ? 'bg-indigo-600 hover:bg-indigo-700 text-white' :
                        'border border-gray-300 hover:border-indigo-600 hover:text-indigo-600 text-gray-700'
                      }`}
                    >
                      {p.cta}
                    </a>

                    {/* Limites técnicos */}
                    <ul className="space-y-2 text-sm mb-4">
                      <Li>📄 {fmtLimit(p.limits.monthlyDocuments)} documentos/mês</Li>
                      <Li>✍ {fmtLimit(p.limits.monthlySignatures)} assinaturas/mês</Li>
                      <Li>👥 Até {fmtLimit(p.limits.maxSignersPerDoc)} signatários por documento</Li>
                      <Li>📁 PDFs de até {p.limits.maxFileSizeMb} MB</Li>
                      <Li>🕐 PDF disponível por {p.limits.retentionDays} dias</Li>
                      {p.limits.apiAccessEnabled && <Li>🔌 API v1 ({p.limits.apiRateLimitPerMin} req/min)</Li>}
                      {p.limits.whiteLabelEnabled && <Li>🎨 White-label (logo + cor)</Li>}
                    </ul>

                    {/* Features extras */}
                    {p.features.length > 0 && (
                      <ul className="space-y-2 text-sm pt-4 border-t border-gray-100">
                        {p.features.map((f, i) => <Li key={i}>{f}</Li>)}
                      </ul>
                    )}
                  </article>
                )
              })}
            </section>

            {/* Comparativo com concorrentes */}
            <section className="bg-white rounded-2xl border border-gray-200 p-6 mb-16">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Como nos comparamos</h2>
              <p className="text-sm text-gray-600 mb-6">
                Tabela honesta. Marcamos o que cada um faz melhor — também onde a concorrência ganha.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 font-semibold text-gray-700">Recurso</th>
                      <th className="text-center py-3 font-semibold text-indigo-600">PAKT</th>
                      <th className="text-center py-3 font-semibold text-gray-600">ClickSign</th>
                      <th className="text-center py-3 font-semibold text-gray-600">DocuSign</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <CmpRow label="Validade jurídica BR (Lei 14.063)" pakt="✓" clicksign="✓" docusign="✓" />
                    <CmpRow label="Autenticação por PIX (R$ 3,90)" pakt="✓" clicksign="—" docusign="—" />
                    <CmpRow label="Selfie + documento de identidade" pakt="✓" clicksign="✓" docusign="✓" />
                    <CmpRow label="Selo PAdES (Adobe Reader)" pakt="✓" clicksign="✓" docusign="✓" />
                    <CmpRow label="Página pública de verificação (/verify)" pakt="✓" clicksign="✓" docusign="—" />
                    <CmpRow label="API v1 + Webhooks" pakt="✓ (STARTER+)" clicksign="✓" docusign="✓ (Enterprise)" />
                    <CmpRow label="Preço inicial mensal" pakt="R$ 79" clicksign="R$ 99" docusign="US$ 25" />
                    <CmpRow label="Trial sem cartão" pakt="✓" clicksign="✓" docusign="—" />
                    <CmpRow label="Cota de docs/mês no básico" pakt="100" clicksign="50" docusign="5" />
                    <CmpRow label="DPA + LGPD-compliant" pakt="✓" clicksign="✓" docusign="✓" />
                  </tbody>
                </table>
              </div>
            </section>

            {/* FAQ */}
            <section className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Perguntas frequentes</h2>
              <div className="space-y-4">
                <Faq q="Posso testar sem cartão?">
                  Sim. As primeiras 5 assinaturas são grátis e não pedimos cartão.
                  Depois disso, escolhe o plano que faz sentido.
                </Faq>
                <Faq q="O R$ 3,90 do signatário é cobrado de quem?">
                  Do signatário, no momento da assinatura, via PIX. É o que valida a identidade
                  bancária — não é cobrado nem do tenant nem da PAKT. Veja
                  {' '}<Link to="/ajuda/pagamento-signatario" className="text-indigo-600 underline">artigo dedicado</Link>.
                </Faq>
                <Faq q="Preciso de NF da PAKT pra deduzir o gasto?">
                  Sim. Emitimos NFS-e automaticamente quando você ativa essa opção no cadastro
                  do tenant (precisa CNPJ cadastrado). Veja
                  {' '}<Link to="/ajuda/nota-fiscal" className="text-indigo-600 underline">como obter</Link>.
                </Faq>
                <Faq q="Posso cancelar a qualquer momento?">
                  Sim, sem multa nem fidelidade. Pagamento anual: reembolso proporcional dos
                  meses não usados, dentro dos primeiros 14 dias.
                </Faq>
                <Faq q="Como vocês cobram o overage?">
                  Não cobramos. Se você passar do limite de docs/mês ou assinaturas/mês,
                  bloqueamos novos envios até o ciclo seguinte (ou você faz upgrade).
                  Nada de surpresa na fatura.
                </Faq>
                <Faq q="Vocês são LGPD-compliant?">
                  Sim. Veja <Link to="/privacidade" className="text-indigo-600 underline">Política de Privacidade</Link>,
                  {' '}<Link to="/dpa" className="text-indigo-600 underline">DPA</Link> e
                  {' '}<Link to="/sub-processadores" className="text-indigo-600 underline">lista de sub-operadores</Link>.
                  Exercer direitos: <Link to="/privacidade/exercer-direitos" className="text-indigo-600 underline">formulário</Link>.
                </Faq>
              </div>
            </section>

            {/* CTA final */}
            <section className="text-center mt-16 pt-12 border-t border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Pronto pra começar?</h2>
              <p className="text-gray-600 mb-6">5 assinaturas grátis pra testar. Sem cartão.</p>
              <Link to="/register" className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold">
                Criar conta grátis
              </Link>
              <p className="text-xs text-gray-500 mt-4">
                Volume? <a href="mailto:comercial@pakt.io" className="text-indigo-600 underline">comercial@pakt.io</a>
              </p>
            </section>
          </>
        )}
      </main>
      <CompanyFooter variant="light" />
    </div>
  )
}

function Li({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-gray-700">
      <span className="text-indigo-600 mt-0.5">✓</span>
      <span>{children}</span>
    </li>
  )
}

function CmpRow({ label, pakt, clicksign, docusign }: {
  label: string; pakt: string; clicksign: string; docusign: string
}) {
  return (
    <tr className="border-b border-gray-100 last:border-0">
      <td className="py-2.5">{label}</td>
      <td className="py-2.5 text-center font-medium text-indigo-600">{pakt}</td>
      <td className="py-2.5 text-center text-gray-500">{clicksign}</td>
      <td className="py-2.5 text-center text-gray-500">{docusign}</td>
    </tr>
  )
}

function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <details className="bg-white rounded-xl border border-gray-200 p-4 group">
      <summary className="font-semibold text-gray-900 cursor-pointer flex justify-between items-center">
        <span>{q}</span>
        <span className="text-gray-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
      </summary>
      <div className="mt-3 text-sm text-gray-600 leading-relaxed">{children}</div>
    </details>
  )
}
